import { useMemo } from "react";
import { Entity } from "@common/types/entity";
import { Lock } from "@common/types/lock";
import { useLockList } from "@api/lock.api";
import { getLockDate, isLockedByDateOnly } from "@helpers/lock";
import { toUTCDateOnly } from "@common/helpers/date";

export function useNonLockedList<
    T extends {
        date: Date;
        dateOnly?: string | null;
        entity: Pick<Entity, "id">;
    },
>(list?: T[]): T[] {
    const uniqueEntityIds = useMemo(
        () => [...new Set((list ?? []).map((l) => l.entity.id))],
        [list],
    );
    const locks = useLockList(uniqueEntityIds);
    const entityLockMap: Map<number, Lock> = useMemo(() => {
        const map = new Map();
        locks.forEach((lock) => {
            if (lock) {
                map.set(lock.entityId, lock);
            }
        });

        return map;
    }, [locks]);

    return useMemo(
        () =>
            (list ?? []).filter((l) => {
                const lock = entityLockMap.get(l.entity.id);
                const lockDate = getLockDate([lock]);
                return !isLockedByDateOnly(
                    lockDate,
                    l.dateOnly ?? toUTCDateOnly(l.date),
                );
            }),
        [list, entityLockMap],
    );
}
